import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Input,
  FormField,
  Alert,
  Box,
  Modal,
  StatusIndicator,
  Cards
} from '@cloudscape-design/components';
import { apiService } from '../../services/api';

interface DesignSpecification {
  baseMapId: string;
  locationListId: string;
  design: {
    physicalSize: {
      width: number;
      height: number;
      unit: 'inches';
    };
    orientation: 'portrait' | 'landscape';
    viewState: {
      centerLat: number;
      centerLong: number;
      zoomLevel: number;
      pixelOffsetX: number;
      pixelOffsetY: number;
    };
    locations: Array<{
      id: string;
      locationId: string;
      name: string;
      lat: number;
      long: number;
      dataProduct: string;
      pixelX: number;
      pixelY: number;
      manuallyAdjusted: boolean;
    }>;
    overlays: Array<{
      id: string;
      type: string;
      visible: boolean;
      position: { x: number; y: number };
      size: { width: number; height: number };
      config: any;
    }>;
  };
}

interface SavedDesign {
  designId: string;
  name: string;
  locationListId: string;
  baseMapId: string;
  design: {
    physicalSize: {
      width: number;
      height: number;
      unit: 'inches';
    };
    orientation: 'portrait' | 'landscape';
    viewState: {
      centerLat: number;
      centerLong: number;
      zoomLevel: number;
      pixelOffsetX: number;
      pixelOffsetY: number;
    };
    locations: Array<{
      id: string;
      locationId: string;
      name: string;
      lat: number;
      long: number;
      dataProduct: string;
      pixelX: number;
      pixelY: number;
      manuallyAdjusted: boolean;
    }>;
    overlays: Array<{
      id: string;
      type: string;
      visible: boolean;
      position: { x: number; y: number };
      size: { width: number; height: number };
      config: any;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

interface DesignPersistenceProps {
  currentDesign: DesignSpecification;
  onLoadDesign: (design: DesignSpecification) => void;
  onSaveSuccess?: () => void;
}

export const DesignPersistence: React.FC<DesignPersistenceProps> = ({
  currentDesign,
  onLoadDesign,
  onSaveSuccess
}) => {
  const [designName, setDesignName] = useState('');
  const [savedDesigns, setSavedDesigns] = useState<SavedDesign[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [designToDelete, setDesignToDelete] = useState<SavedDesign | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load saved designs for the current location list
  useEffect(() => {
    if (currentDesign.locationListId) {
      loadSavedDesigns();
    }
  }, [currentDesign.locationListId]);

  const loadSavedDesigns = async () => {
    setLoadingDesigns(true);
    
    try {
      const response = await apiService.getUserDesigns(currentDesign.locationListId);
      
      if (response.statusCode !== 200 || !response.data) {
        throw new Error(response.error || 'Failed to load saved designs');
      }
      
      const designs = response.data.designs || [];
      setSavedDesigns(designs);
    } catch (error) {
      console.error('Error loading saved designs:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load saved designs'
      });
    } finally {
      setLoadingDesigns(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      setAlert({ type: 'error', message: 'Please enter a design name' });
      return;
    }
    
    setLoading(true);
    setAlert(null);
    
    try {
      // currentDesign already has the correct structure:
      // { baseMapId, locationListId, design: { physicalSize, orientation, viewState, locations, overlays } }
      const designData = {
        name: designName.trim(),
        ...currentDesign  // Spread to include baseMapId, locationListId, and design
      };
      
      const response = await apiService.saveDesign(designData);
      
      if (response.statusCode !== 200 || !response.data) {
        throw new Error(response.error || 'Failed to save design');
      }
      
      setAlert({ type: 'success', message: `Design "${designName}" saved successfully` });
      setDesignName('');
      
      // Reload saved designs list
      await loadSavedDesigns();
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error saving design:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save design'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDesign = (savedDesign: SavedDesign) => {
    // Reconstruct the full DesignSpecification structure
    const designSpec: DesignSpecification = {
      baseMapId: savedDesign.baseMapId,
      locationListId: savedDesign.locationListId,
      design: savedDesign.design
    };
    onLoadDesign(designSpec);
    setAlert({ type: 'success', message: `Design "${savedDesign.name}" loaded successfully` });
  };

  const handleDeleteClick = (design: SavedDesign) => {
    setDesignToDelete(design);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!designToDelete) return;
    
    setDeleting(true);
    
    try {
      const response = await apiService.deleteDesign(designToDelete.designId);
      
      if (response.statusCode !== 200) {
        throw new Error(response.error || 'Failed to delete design');
      }
      
      setAlert({ type: 'success', message: `Design "${designToDelete.name}" deleted successfully` });
      
      // Remove from local state
      setSavedDesigns(savedDesigns.filter(d => d.designId !== designToDelete.designId));
      
      setDeleteModalVisible(false);
      setDesignToDelete(null);
    } catch (error) {
      console.error('Error deleting design:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete design'
      });
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h2">Saved Print Maps</Header>

        {alert && (
          <Alert
            type={alert.type}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}

        {/* Save Design Section */}
        <Box>
          <SpaceBetween size="m">
            <Header variant="h3">Save Current Design</Header>
            <FormField
              label="Design Name"
              description="Enter a name for this design"
            >
              <Input
                value={designName}
                onChange={({ detail }) => setDesignName(detail.value)}
                placeholder="e.g., My Map Design"
                disabled={loading}
              />
            </FormField>
            <Button
              variant="primary"
              onClick={handleSaveDesign}
              loading={loading}
              disabled={!designName.trim()}
            >
              Save Design
            </Button>
          </SpaceBetween>
        </Box>

        {/* Load Design Section */}
        <Box>
          <SpaceBetween size="m">
            <Header
              variant="h3"
              actions={
                <Button
                  iconName="refresh"
                  onClick={loadSavedDesigns}
                  loading={loadingDesigns}
                >
                  Refresh
                </Button>
              }
            >
              Saved Designs
            </Header>

            {loadingDesigns ? (
              <Box textAlign="center" padding="l">
                <StatusIndicator type="loading">Loading saved designs...</StatusIndicator>
              </Box>
            ) : savedDesigns.length === 0 ? (
              <Alert type="info">
                No saved designs for this location list. Save your current design to get started.
              </Alert>
            ) : (
              <Cards
                cardDefinition={{
                  header: (item: SavedDesign) => (
                    <Box>
                      <strong>{item.name}</strong>
                    </Box>
                  ),
                  sections: [
                    {
                      id: 'details',
                      content: (item: SavedDesign) => (
                        <SpaceBetween size="xs">
                          <Box variant="small">
                            <strong>Size:</strong> {item.design.physicalSize.width}" × {item.design.physicalSize.height}"
                          </Box>
                          <Box variant="small">
                            <strong>Orientation:</strong> {item.design.orientation}
                          </Box>
                          <Box variant="small">
                            <strong>Locations:</strong> {item.design.locations.length}
                          </Box>
                          <Box variant="small">
                            <strong>Overlays:</strong> {item.design.overlays.filter(o => o.visible).length}
                          </Box>
                          <Box variant="small" color="text-status-inactive">
                            Saved: {formatDate(item.createdAt)}
                          </Box>
                          <Box>
                            <SpaceBetween direction="horizontal" size="xs">
                              <Button
                                onClick={() => handleLoadDesign(item)}
                                variant="primary"
                              >
                                Load
                              </Button>
                              <Button
                                onClick={() => handleDeleteClick(item)}
                              >
                                Delete
                              </Button>
                            </SpaceBetween>
                          </Box>
                        </SpaceBetween>
                      )
                    }
                  ]
                }}
                items={savedDesigns}
                cardsPerRow={[{ cards: 1 }]}
              />
            )}
          </SpaceBetween>
        </Box>

        {/* Delete Confirmation Modal */}
        <Modal
          visible={deleteModalVisible}
          onDismiss={() => {
            setDeleteModalVisible(false);
            setDesignToDelete(null);
          }}
          header="Delete Design"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => {
                    setDeleteModalVisible(false);
                    setDesignToDelete(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  loading={deleting}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {designToDelete && (
            <Box>
              Are you sure you want to delete the design "{designToDelete.name}"? This action cannot be undone.
            </Box>
          )}
        </Modal>
      </SpaceBetween>
    </Container>
  );
};

export default DesignPersistence;
